// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AskV2Hero } from "./AskV2Hero";
import { type AskV2ComposerMode } from "./ComposerModeToggle";
import { useAskV2HomeCopy } from "./useAskV2HomeCopy";
import { WelcomeBriefing } from "./WelcomeBriefing";

import {
  CreateAskPunttProjectDialog,
  HomeProjectPicker,
} from "../../components/ask-puntt-home";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { invalidateAskPunttUnreadLists } from "../../utils/queries/askPuntt";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { ASK_V2_CHAT_PATH, ASK_V2_PROJECT_PATH } from "../paths";
import { usePostToAskV2ProjectSharedFeed } from "../services/postToAskV2ProjectSharedFeed";
import { createAskV2Project } from "../services/projects";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { useAskV2Transport } from "../transport/useAskV2Transport";

const HOME_PROJECT_LANE_SPACER_CLASS = "h-10";
const PROJECT_LANE_EXIT_MS = 220;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function AskV2Home() {
  const transport = useAskV2Transport();
  const postToProjectFeed = usePostToAskV2ProjectSharedFeed();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const analytics = useAnalytics("ask_puntt_v2_home");
  const homeCopy = useAskV2HomeCopy();
  const navigatedToChatRef = useRef(false);
  const [composerMode, setComposerMode] = useState<AskV2ComposerMode>("chat");
  // why: the create ACK flips transport.chatId to non-null one commit BEFORE
  // the navigate effect can run (effects fire after paint). Deriving the
  // hero-hide from a useState set inside that effect leaves a single painted
  // frame where chatId is set but the flag is still false — the landing hero
  // flashes back over the thread we're about to route to. Latch the exit
  // synchronously during render instead: once this home has a thread AND (a
  // chatId has arrived OR chat-mode navigation already began), the hero stays
  // hidden for that same commit and every commit until this component unmounts.
  const exitingToChatRef = useRef(false);
  if (
    transport.messages.length > 0 &&
    (transport.chatId != null || navigatedToChatRef.current) &&
    composerMode !== "project"
  ) {
    exitingToChatRef.current = true;
  }
  const showPreNavigateThread =
    transport.messages.length > 0 &&
    (transport.chatId == null || exitingToChatRef.current);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(
    null,
  );
  const [selectedSharedChatId, setSelectedSharedChatId] = useState<
    string | null
  >(null);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectLaneMounted, setProjectLaneMounted] = useState(false);
  const [projectLaneExiting, setProjectLaneExiting] = useState(false);
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const projectLaneExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    const chatId = transport.chatId;
    if (!chatId || navigatedToChatRef.current || composerMode === "project") {
      return;
    }
    navigatedToChatRef.current = true;
    invalidateAskPunttUnreadLists(queryClient);
    void navigate({
      to: ASK_V2_CHAT_PATH,
      params: { chatId },
      replace: true,
    });
  }, [composerMode, navigate, queryClient, transport.chatId]);

  useEffect(() => {
    if (composerMode === "project") {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
      setProjectLaneMounted(true);
      setProjectLaneExiting(false);
      return;
    }
    if (!projectLaneMounted) return;
    if (prefersReducedMotion()) {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      return;
    }
    setProjectLaneExiting(true);
    projectLaneExitTimerRef.current = setTimeout(() => {
      setProjectLaneMounted(false);
      setProjectLaneExiting(false);
      projectLaneExitTimerRef.current = null;
    }, PROJECT_LANE_EXIT_MS);
    return () => {
      if (projectLaneExitTimerRef.current != null) {
        clearTimeout(projectLaneExitTimerRef.current);
        projectLaneExitTimerRef.current = null;
      }
    };
  }, [composerMode, projectLaneMounted]);

  const needsProjectSelection =
    composerMode === "project" && selectedProjectId == null;

  const handleProjectSubmit = async (input: {
    message: string;
    attachments: import("../services/chat").AskV2Attachment[];
  }): Promise<boolean> => {
    if (needsProjectSelection || projectSubmitting) return false;
    if (!selectedProjectId) return false;

    const submittedMessage = input.message;
    setProjectSubmitting(true);
    try {
      // why: home project-mode submit has no thread to highlight, and this
      // branch renders no TextSelectionToolbar (the landing SummonBar never
      // wires onAddQuotedContext), so transport.quotedContexts is always empty
      // here — forwarding it was dead code. Quoting is a chat-page / project-
      // page surface; do not claim it on the home landing.
      await postToProjectFeed.mutateAsync({
        projectId: selectedProjectId,
        sharedChatId: selectedSharedChatId,
        message: input.message,
        attachments:
          input.attachments.length > 0 ? input.attachments : undefined,
      });
      analytics.capture("ask_puntt_chat_created", {
        workflow_type: "general",
        message_length: input.message.length,
        has_attachments: input.attachments.length > 0,
        attachment_count: input.attachments.length,
        project_id: selectedProjectId,
        project_chat_kind: "shared",
      });
      if (transport.composer === submittedMessage) {
        transport.setComposer("");
      }
      transport.clearQuotedContexts();
      void navigate({
        to: ASK_V2_PROJECT_PATH,
        params: { projectId: selectedProjectId },
      });
      return true;
    } catch (error) {
      analytics.capture("ask_puntt_chat_create_error", {
        workflow_type: "general",
        has_attachments: input.attachments.length > 0,
        attachment_count: input.attachments.length,
        project_id: selectedProjectId,
        ...errorAnalyticsPayload(error),
      });
      return false;
    } finally {
      setProjectSubmitting(false);
    }
  };

  if (showPreNavigateThread) {
    return (
      <AskV2ConversationShell
        testId="ask-v2-home"
        header={null}
        scrollResetKey={transport.chatId ?? "home-draft"}
        scrollDeps={[transport.displayMessages]}
        autoScrollEnabled={!transport.inFlight}
        composer={<AskV2SummonBar transport={transport} />}
      >
        <AskV2Thread transport={transport} />
      </AskV2ConversationShell>
    );
  }

  return (
    <div
      data-testid="ask-v2-home"
      className="ask-puntt-surface relative flex size-full min-h-0 flex-col overflow-hidden"
    >
      <div
        className="mx-auto flex size-full min-h-0 max-w-[900px] flex-1 flex-col overflow-y-auto px-4"
        data-testid="ask-v2-home-main-column"
      >
        <div
          className="min-h-0 flex-1"
          aria-hidden="true"
          data-testid="ask-v2-home-top-flex-spacer"
        />
        <AskV2Hero
          headline={homeCopy.copy?.headline ?? null}
          accentWord={homeCopy.copy?.accentWord ?? null}
        />
        <div className="relative z-10 w-full shrink-0">
          <div className="relative">
            <AskV2SummonBar
              landing
              transport={transport}
              composerMode={composerMode}
              onComposerModeChange={setComposerMode}
              needsProjectSelection={needsProjectSelection}
              projectSubmitting={projectSubmitting}
              onProjectSubmit={handleProjectSubmit}
            />
            {projectLaneMounted ? (
              <div
                className={
                  projectLaneExiting
                    ? "animate-ask-puntt-home-project-lane-out absolute inset-x-0 top-full z-10 origin-top"
                    : "animate-ask-puntt-home-project-lane-in absolute inset-x-0 top-full z-10 origin-top"
                }
                data-testid="ask-v2-home-project-lane"
                data-exiting={projectLaneExiting ? "true" : "false"}
                onAnimationEnd={() => {
                  if (!projectLaneExiting) return;
                  if (projectLaneExitTimerRef.current != null) {
                    clearTimeout(projectLaneExitTimerRef.current);
                    projectLaneExitTimerRef.current = null;
                  }
                  setProjectLaneMounted(false);
                  setProjectLaneExiting(false);
                }}
              >
                <HomeProjectPicker
                  testIdPrefix="ask-v2-home"
                  selectedProjectId={selectedProjectId}
                  selectedProjectName={selectedProjectName}
                  onSelect={(project) => {
                    setSelectedProjectId(project.id);
                    setSelectedProjectName(project.name);
                    setSelectedSharedChatId(project.sharedChatId);
                  }}
                  onClear={() => {
                    setSelectedProjectId(null);
                    setSelectedProjectName(null);
                    setSelectedSharedChatId(null);
                  }}
                  onNewProject={() => setCreateProjectOpen(true)}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div
          className="flex flex-col items-center gap-6 py-8"
          data-testid="ask-v2-home-below-composer"
        >
          {projectLaneMounted ? (
            <div
              aria-hidden="true"
              data-testid="ask-v2-home-project-lane-spacer"
              className={HOME_PROJECT_LANE_SPACER_CLASS}
            />
          ) : null}
          <WelcomeBriefing
            pending={homeCopy.pending}
            lead={homeCopy.copy?.subheading ?? null}
            disabled={
              transport.sending ||
              transport.inFlight ||
              projectSubmitting ||
              needsProjectSelection
            }
            onSelect={(prompt) => {
              if (composerMode === "project") {
                void handleProjectSubmit({ message: prompt, attachments: [] });
                return;
              }
              transport.setComposer(prompt);
              void transport.send();
            }}
          />
        </div>
        <div className="min-h-0 flex-1" aria-hidden="true" />
      </div>
      <CreateAskPunttProjectDialog
        open={createProjectOpen}
        onOpenChange={setCreateProjectOpen}
        createFn={createAskV2Project}
        onCreated={(project) => {
          setSelectedProjectId(project.id);
          setSelectedProjectName(project.name);
          setSelectedSharedChatId(project.sharedChatId);
        }}
      />
    </div>
  );
}
